import { AlertTriangle, ChevronDown, Loader2, ShieldCheck } from "lucide-react";

const SECURITY_ITEMS = [
  { key: "enable_ja3", label: "JA3 blocking" },
  { key: "enable_ja4", label: "JA4 blocking" },
  { key: "enable_header_anomaly", label: "Header anomaly" },
  { key: "enable_crawler_verify", label: "Crawler verify" },
  { key: "enable_fp_velocity", label: "FP velocity" },
  { key: "enable_geo_consistency", label: "Geo consistency" },
  { key: "second_hop_cloak", label: "Second-hop cloak" },
];

const DefenseToggle = ({ item, enabled, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(item.key, !enabled)}
    className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs transition-colors ${enabled ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-200" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}
    data-testid={`defense-default-${item.key}`}
    aria-pressed={enabled}
  >
    <span>{item.label}</span>
    <span className={`h-2 w-2 rounded-full ${enabled ? "bg-cyan-400" : "bg-slate-700"}`} />
  </button>
);

const CandidateRow = ({ candidate, reviewing, onReview }) => (
  <div
    className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between"
    data-testid={`ja3-candidate-${candidate.ja3}`}
  >
    <div className="min-w-0">
      <p className="truncate font-mono text-xs text-cyan-300">{candidate.ja3}</p>
      <p className="mt-1 truncate text-xs text-slate-500">
        {candidate.hits} hits · {candidate.last_ua || "Unknown user agent"}
      </p>
    </div>
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onReview(candidate.ja3, "approve")}
        disabled={reviewing === candidate.ja3}
        className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-300 disabled:opacity-40"
        data-testid={`ja3-approve-${candidate.ja3}`}
      >
        {reviewing === candidate.ja3 ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Block"}
      </button>
      <button
        type="button"
        onClick={() => onReview(candidate.ja3, "reject")}
        disabled={reviewing === candidate.ja3}
        className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 disabled:opacity-40"
        data-testid={`ja3-reject-${candidate.ja3}`}
      >
        Dismiss
      </button>
    </div>
  </div>
);

export const UserSecurityOperations = ({
  open,
  onToggle,
  defaults,
  onDefaultsChange,
  onApply,
  applying,
  message,
  candidates,
  reviewing,
  onReview,
}) => {
  const changeDefault = (key, enabled) => {
    onDefaultsChange({ ...defaults, [key]: enabled });
  };
  const defenseToggles = [];
  for (const item of SECURITY_ITEMS) {
    defenseToggles.push(
      <DefenseToggle
        key={item.key}
        item={item}
        enabled={!!defaults[item.key]}
        onChange={changeDefault}
      />
    );
  }
  const candidateRows = [];
  for (const candidate of candidates) {
    candidateRows.push(
      <CandidateRow
        key={candidate.ja3}
        candidate={candidate}
        reviewing={reviewing}
        onReview={onReview}
      />
    );
  }

  return (
    <section className="rounded-md border border-slate-800 bg-[#0C121D]" data-testid="defense-defaults-card">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
        data-testid="defense-defaults-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-md bg-cyan-500/10 text-cyan-300">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase text-cyan-400">Fleet policy</p>
            <h2 className="mt-1 text-base font-semibold text-slate-100">Next-Gen bot defense defaults</h2>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-slate-800 p-5" data-testid="defense-defaults-content">
          <p className="text-sm text-slate-500">
            Choose the default defenses and push them to a user cohort. Users may still override their own settings.
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {defenseToggles}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onApply("licensed")}
              disabled={applying}
              className="btn-primary disabled:opacity-40"
              data-testid="apply-defense-licensed"
            >
              {applying ? "Applying" : "Apply to licensed"}
            </button>
            <button
              type="button"
              onClick={() => onApply("all")}
              disabled={applying}
              className="btn-secondary disabled:opacity-40"
              data-testid="apply-defense-all"
            >
              Apply to all users
            </button>
            {message && <span className="text-xs text-slate-400" data-testid="defense-apply-msg">{message}</span>}
          </div>

          {candidates.length > 0 && (
            <div className="mt-6 border-t border-slate-800 pt-5" data-testid="ja3-review-card">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <p className="text-sm font-semibold text-slate-200">JA3 auto-learn review</p>
                <span className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-300">
                  {candidates.length}
                </span>
              </div>
              <div className="mt-3 divide-y divide-slate-800">
                {candidateRows}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};